import { action, observable, runInAction, computed } from 'mobx';
import { message } from 'antd';
import TrainingProjectsStore from '../stores/TrainingProjectsStore';
import TrainingProjectsService from '../services/TrainingProjectsService';
import { formatLastUpdateRelative } from '../../common/Utils';
import {
    TrainingProjectDto,
    TrainingProjectTaskType,
    CreateEditTrainingProjectFormValues,
    TrainingProjectOptimizerType,
    TrainingProjectModuleType,
    TrainingProjectAdapterType
} from '../types';
import { showRuntimeSchemaValidationError } from '../utils/runtimeSchemaValidation';

type TaskType = TrainingProjectTaskType;

type OptimizerType = TrainingProjectOptimizerType;

type CreateEditFormValues = CreateEditTrainingProjectFormValues;

type ModuleType = TrainingProjectModuleType;

type AdapterType = TrainingProjectAdapterType;

export default class TrainingProjectModel {
    @observable
    name: string;

    @observable
    updatedBy?: string;

    @observable
    updateDate?: Date;

    @observable
    systemPrompt?: string;

    @observable
    referenceModelId: string;

    @observable
    teacherModelId?: string;

    @observable
    judgeModelId?: string;

    @observable
    taskType: TaskType;

    @observable
    optimizerType?: OptimizerType;

    @observable
    optimizerParams?: string;

    @observable
    jsonSchemas?: Record<string, string>;

    @observable
    fieldDescriptions?: Record<string, string>;

    @observable
    moduleType?: ModuleType;

    @observable
    adapterType?: AdapterType;

    @observable
    isEditable: boolean = false;

    readonly id: string;

    readonly projectId: string;

    readonly createdBy: string;

    readonly createDate: Date;

    constructor(
        private readonly store: TrainingProjectsStore,
        private readonly service: TrainingProjectsService,
        data: TrainingProjectDto
    ) {
        this.id = data.id;
        this.projectId = data.projectId;
        this.name = data.name;
        this.systemPrompt = data.systemPrompt;
        this.referenceModelId = data.referenceModelId;
        this.teacherModelId = data.teacherModelId;
        this.judgeModelId = data.judgeModelId;
        this.taskType = data.taskType;
        this.optimizerType = data.optimizerType;
        this.optimizerParams = data.optimizerParams;
        this.jsonSchemas = data.jsonSchemas;
        this.fieldDescriptions = data.fieldDescriptions;
        this.moduleType = data.moduleType;
        this.adapterType = data.adapterType;
        this.createdBy = data.createdBy;
        this.updatedBy = data.updatedBy;
        this.createDate = new Date(data.createDate);
        this.updateDate = data.updateDate ? new Date(data.updateDate) : undefined;
    }

    @computed
    get status() {
        return this.store.trainingProjectsStatuses.find(status => status.trainingProjectId === this.id);
    }

    @computed
    get statusTooltip() {
        return formatLastUpdateRelative(this.status?.latestSessionUpdateDate);
    }

    @action.bound
    setEditable(isEditable: boolean) {
        this.isEditable = isEditable;
    }

    @action.bound
    async update(formValues: CreateEditFormValues) {
        if (!this.id) {
            return;
        }

        const resp = await this.service.updateTrainingProject(this.projectId, this.id, formValues);

        if (!resp.isOk()) {
            if (!showRuntimeSchemaValidationError(resp.error.data)) {
                message.error('Failed to update training project');
            }
            return false;
        }

        resp.map(data => this.updateData(data));

        return true;
    }

    @action.bound
    async delete() {
        if (!this.id) {
            return;
        }

        const resp = await this.service.deleteTrainingProject(this.projectId, this.id);

        if (!resp.isOk()) {
            message.error('Failed to delete training project');
            return;
        }

        this.store.removeTrainingProject(this);
    }

    updateData(data: TrainingProjectDto) {
        runInAction(() => {
            this.name = data.name;
            this.systemPrompt = data.systemPrompt;
            this.referenceModelId = data.referenceModelId;
            this.teacherModelId = data.teacherModelId;
            this.judgeModelId = data.judgeModelId;
            this.taskType = data.taskType;
            this.optimizerType = data.optimizerType;
            this.optimizerParams = data.optimizerParams;
            this.jsonSchemas = data.jsonSchemas;
            this.fieldDescriptions = data.fieldDescriptions;
            this.moduleType = data.moduleType;
            this.adapterType = data.adapterType;
            this.updatedBy = data.updatedBy;
            this.updateDate = data.updateDate ? new Date(data.updateDate) : undefined;
        });

        this.store.refreshTrainingProjects();
    }
}
